import React, { useState, useEffect, useRef } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import VoiceInput from '../../components/practice/VoiceInput';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const styles = {
  layout: { display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '16px',
  },
  title: { fontSize: '20px', fontWeight: '700', color: '#111827' },
  meta: { fontSize: '13px', color: '#6b7280' },
  endBtn: {
    backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5',
    borderRadius: '8px', padding: '8px 16px', fontSize: '13px',
    fontWeight: '600', cursor: 'pointer',
  },
  chat: {
    flex: 1, overflowY: 'auto', padding: '16px 0',
    display: 'flex', flexDirection: 'column', gap: '16px',
  },
  bubble: { display: 'flex', gap: '12px', maxWidth: '75%' },
  bubbleAI: { alignSelf: 'flex-start' },
  bubbleUser: { alignSelf: 'flex-end', flexDirection: 'row-reverse' },
  avatar: {
    width: '36px', height: '36px', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '16px', flexShrink: 0,
  },
  avatarAI: { backgroundColor: '#EBF5FF' },
  avatarUser: { backgroundColor: '#0052CC' },
  msgContent: { display: 'flex', flexDirection: 'column', gap: '4px' },
  msgRole: { fontSize: '11px', fontWeight: '700', color: '#9ca3af', letterSpacing: '0.5px' },
  msgText: {
    padding: '12px 16px', borderRadius: '12px',
    fontSize: '14px', lineHeight: '1.6',
  },
  msgTextAI: { backgroundColor: '#f3f4f6', color: '#111827' },
  msgTextUser: { backgroundColor: '#0052CC', color: '#ffffff' },
  audioPlayer: { marginTop: '6px', width: '100%', height: '28px' },
  inputArea: {
    borderTop: '1px solid #e5e7eb', paddingTop: '20px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
  },
  inputHint: { fontSize: '13px', color: '#9ca3af' },
  processing: {
    display: 'flex', alignItems: 'center', gap: '8px',
    fontSize: '13px', color: '#6b7280', padding: '8px 0',
  },
  dot: {
    width: '8px', height: '8px', borderRadius: '50%',
    backgroundColor: '#0052CC', animation: 'bounce 1.4s infinite',
  },
  error: {
    backgroundColor: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626',
    borderRadius: '8px', padding: '10px 14px', fontSize: '13px',
    width: '100%', boxSizing: 'border-box',
  },
};

export default function PracticeSession() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [turns, setTurns] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const chatEndRef = useRef(null);
  const audioRef = useRef(null);
  const location = useLocation();

  useEffect(() => { fetchSession(); }, [id]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [turns]);

  async function fetchSession() {
    const [sessionRes, turnsRes] = await Promise.all([
      fetch(`${API}/api/practice/sessions/${id}`),
      fetch(`${API}/api/practice/sessions/${id}/turns`),
    ]);
    const sessionData = await sessionRes.json();
    const turnsData = await turnsRes.json();
    setSession(sessionData);
    setTurns(Array.isArray(turnsData) ? turnsData : []);

    // Only play opening audio on fresh session start (not on resume)
    const openingAudio = location.state?.opening_audio;
    const isFirstVisit = !!openingAudio;
    if (isFirstVisit) {
      setTimeout(() => playAudio(openingAudio), 500);
      // Clear state so refresh/resume doesn't replay
      window.history.replaceState({}, document.title);
    }
  }

  function playAudio(urlOrBase64) {
    if (!urlOrBase64) return;
    if (audioRef.current) {
      audioRef.current.pause();
    }
    // Handle both base64 and URL
    const src = urlOrBase64.startsWith('http')
      ? urlOrBase64
      : `data:audio/mpeg;base64,${urlOrBase64}`;
    const audio = new Audio(src);
    audioRef.current = audio;
    audio.play().catch(err => console.error('Audio play error:', err));
  }

  async function handleTranscribed(text) {
    if (!text?.trim()) return;
    setIsProcessing(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/practice/sessions/${id}/turn`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error('Failed to process turn');
      const data = await res.json();

      setTurns(prev => [...prev, data.user_turn, data.ai_turn]);

      if (data.ai_turn.audio_base64) {
        setTimeout(() => playAudio(data.ai_turn.audio_base64), 300);
      }
    } catch (err) {
      setError('Failed to process your response. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }

  async function handleEnd() {
    if (!window.confirm('End this session and get your feedback report?')) return;
    setIsProcessing(true);
    try {
      await fetch(`${API}/api/practice/sessions/${id}/end`, { method: 'POST' });
      navigate(`/practice/report/${id}`);
    } catch {
      setError('Failed to end session.');
      setIsProcessing(false);
    }
  }

  if (!session) return <AppLayout><div style={{ padding: '32px', color: '#6b7280' }}>Loading session...</div></AppLayout>;

  return (
    <AppLayout>
      <div style={styles.layout}>
        <div style={styles.header}>
          <div>
            <div style={styles.title}>{session.template_title}</div>
            <div style={styles.meta}>
              AI: {session.llm_provider} · STT: {session.stt_provider} · {turns.length} turns
            </div>
            {session.goals && (
              <div style={{ fontSize: '12px', color: '#0052CC', marginTop: '4px', fontWeight: '500' }}>
                🎯 {session.goals}
              </div>
            )}
          </div>
          <button style={styles.endBtn} onClick={handleEnd} disabled={isProcessing}>
            End & Get Feedback
          </button>
        </div>

        <style>{`
          @keyframes bounce {
            0%, 80%, 100% { transform: scale(0); }
            40% { transform: scale(1); }
          }
        `}</style>

        <div style={styles.chat}>
          {turns.map((turn, idx) => {
            const isAI = turn.role === 'ai';
            return (
              <div key={idx} style={{ ...styles.bubble, ...(isAI ? styles.bubbleAI : styles.bubbleUser) }}>
                <div style={{ ...styles.avatar, ...(isAI ? styles.avatarAI : styles.avatarUser) }}>
                  {isAI ? '🤖' : '👤'}
                </div>
                <div style={styles.msgContent}>
                  <div style={styles.msgRole}>{isAI ? session.template_title?.toUpperCase() : 'YOU'}</div>
                  <div style={{ ...styles.msgText, ...(isAI ? styles.msgTextAI : styles.msgTextUser) }}>
                    {turn.content}
                  </div>
                </div>
              </div>
            );
          })}

          {isProcessing && (
            <div style={styles.processing}>
              <div style={{ ...styles.dot, animationDelay: '0s' }} />
              <div style={{ ...styles.dot, animationDelay: '0.2s' }} />
              <div style={{ ...styles.dot, animationDelay: '0.4s' }} />
              <span>Processing...</span>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        <div style={styles.inputArea}>
          {error && <div style={styles.error}>{error}</div>}
          <VoiceInput onTranscribed={handleTranscribed} disabled={isProcessing} language={session?.language} />
          <div style={styles.inputHint}>
            {turns.length === 0 && session?.user_initiates
              ? '🎙 You speak first — press the button to start'
              : 'Press to speak, press again to send'}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
